import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { AbstractService } from "src/app/common/services/abstract.service";
import { SavedChat } from "src/app/modules/prompts/models/backend-saved-chat.model";
import { PredefinedPrompt, PromptModel } from "src/app/modules/prompts/models/prompt-template.model";
import { UserPrompt } from "src/app/modules/prompts/models/user.prompt.model";

const API_PATH = "/api/internal/prompts";

@Injectable({
  providedIn: "root",
})
export class PromptsService extends AbstractService {
  private readonly httpClient = inject(HttpClient);
  readonly predefinedPromptsChanged = new EventEmitter<void>();
  readonly restoreHistory = new EventEmitter<SavedChat>();

  async saveChat(savedChat: SavedChat): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${API_PATH}/${savedChat.id}`, savedChat));
  }

  async listAll(params: HttpParams): Promise<SavedChat[] | null> {
    return await this.cancelPrevious("listAll", this.httpClient.get<SavedChat[]>(`${API_PATH}/`, { params }));
  }

  async deleteAllInteractions(): Promise<void> {
    await this.cancelPrevious("deleteAllInteractions", this.httpClient.delete(`${API_PATH}/delete-interactions`));
  }

  async deleteInteraction(id: string): Promise<void> {
    await this.cancelPrevious("deleteInteraction", this.httpClient.delete(`${API_PATH}/${id}`));
  }

  async count(params: HttpParams): Promise<number> {
    return await firstValueFrom(this.httpClient.get<number>(`${API_PATH}/size`, { params }));
  }

  async listTemplates(params: HttpParams): Promise<PromptModel[]> {
    return await firstValueFrom(this.httpClient.get<PromptModel[]>(`${API_PATH}/templates`, { params }));
  }

  async saveUserPrompt(parentId: string, userPrompt: UserPrompt): Promise<void> {
    await firstValueFrom(this.httpClient.post<UserPrompt>(`${API_PATH}/user-prompts/${parentId}`, userPrompt));
  }

  async isUserPrompt(id: string): Promise<boolean> {
    return await firstValueFrom(this.httpClient.get<boolean>(`${API_PATH}/template-prompts/is-user-prompt/${id}`));
  }

  async deleteUserPrompt(id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/user-prompts/${id}`));
  }

  async saveTemplatePrompt(parentId: string, predefinedPrompt: PredefinedPrompt): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${API_PATH}/template-prompts/${parentId}`, predefinedPrompt));
  }

  async addTemplateNode(parentNodeId: string, label: string): Promise<PromptModel> {
    return await firstValueFrom(
      this.httpClient.post<PromptModel>(`${API_PATH}/template-prompts/add/${parentNodeId}`, label),
    );
  }

  async moveGroup(childId: string, parentId: string): Promise<void> {
    await firstValueFrom(this.httpClient.put(`${API_PATH}/move-group/${parentId}/${childId}`, ""));
  }

  async movePrompt(childId: string, parentId: string): Promise<void> {
    await firstValueFrom(this.httpClient.put(`${API_PATH}/move-prompt/${parentId}/${childId}`, ""));
  }

  async editTemplateNode(id: string, label: string): Promise<PromptModel> {
    return await firstValueFrom(this.httpClient.put<PromptModel>(`${API_PATH}/template-prompts/${id}`, label));
  }

  async deleteTemplateNode(id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/template-prompts/${id}`));
  }

  async deleteTemplatePrompt(parentId: string, id: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/template-prompts/${parentId}/${id}`));
  }

  async listFavoritesPrompts(): Promise<string[]> {
    return await firstValueFrom(this.httpClient.get<string[]>(`${API_PATH}/list-favorites`));
  }

  async setFavoritePrompt(promptId: string, favorite: boolean): Promise<void> {
    await firstValueFrom(this.httpClient.post(`${API_PATH}/set-favorite/${promptId}`, favorite));
  }

  async copyToMyPrompts(promptId: string): Promise<void> {
    await firstValueFrom(this.httpClient.put(`${API_PATH}/copy-to-myprompts/${promptId}`, ""));
  }
}
